import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRetweet } from '@soundxyz/font-awesome-new/pro-regular-svg-icons/faArrowsRetweet.js';
import { isRepostablePost } from '@components/Feed/Post/ActionButtons';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import {
  type FragmentType,
  getFragment,
  gql,
  OriginalPostInfoFragmentDoc,
  RepostSourceFragmentDoc,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { PostItem } from '../Post/PostItem';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment RepostSource on Repost {
    id
    creator {
      id
      username
      webappUri
    }
    ...PostItemRepostSource
  }

  fragment OriginalPostInfo on Post {
    __typename
    id
    webappUri
    creator {
      id
      ...FeedActivityItemUserInfo
    }
    ...PostGroupItem
  }
`);

export const Repost = ({
  originalPost,
  originalTimeStamp,
  repostSource,
}: {
  originalTimeStamp: number;
  originalPost: FragmentType<OriginalPostInfoFragmentDoc>;
  repostSource: FragmentType<RepostSourceFragmentDoc>;
}) => {
  const originalPostInfo = getFragment(OriginalPostInfoFragmentDoc, originalPost);

  const repostSourceInfo = getFragment(RepostSourceFragmentDoc, repostSource);

  const userHoverCardContent = useMemo(
    () => <UserHoverCardContent userId={repostSourceInfo.creator.id} />,
    [repostSourceInfo.creator.id],
  );

  if (!isRepostablePost(originalPostInfo)) return null;

  return (
    <div className="mt-[-15px] flex flex-col gap-2 ">
      <div className="flex flex-row items-center gap-2 pb-2 pl-4 align-middle text-base-m text-base500 md:pl-8">
        <FontAwesomeIcon icon={faArrowsRetweet} className="text-base500" fontSize={20} />
        <UserHoverCard
          trigger={
            <span className="font-medium">
              <AccessibleLink
                pathnameUri={webappUriPath(repostSourceInfo.creator.webappUri)}
                className="font-semibold transition-[filter] duration-300 hover:brightness-70"
              >
                {repostSourceInfo.creator.username}
              </AccessibleLink>{' '}
              reposted
            </span>
          }
          userHoverCardContent={userHoverCardContent}
        />
      </div>
      <PostItem
        creator={originalPostInfo.creator}
        post={originalPostInfo}
        postId={originalPostInfo.id}
        timeStamp={originalTimeStamp}
        webappUri={originalPostInfo.webappUri}
        repostSource={repostSourceInfo}
        variant="feed"
      />
    </div>
  );
};
